// AIQuestionsEditor.js - Fully updated component with testing integration
import React, { useState, useEffect, useCallback, memo } from 'react';
import { Form, Card, Button, Space, Typography, Alert, Spin, Row, Col, Input } from 'antd';
import { PlusOutlined, PlayCircleOutlined, BugOutlined } from '@ant-design/icons';
import AIQuestionEditor from './AIQuestionEditor';

const { Text } = Typography;

// Memoized version of AIQuestionEditor for better performance
const MemoizedAIQuestionEditor = memo(AIQuestionEditor);

// Memoized question item to prevent re-renders of the entire list
const QuestionItem = memo(({ field, index, totalFields, remove, add, getInitialQuestion, testResults, testErrors, testingInProgress }) => {
    return (
        <Card
            key={field.key}
            size="small"
            style={{
                marginBottom: '12px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
            bodyStyle={{ padding: '12px' }}
        >
            <MemoizedAIQuestionEditor
                namePrefix={[field.name]}
                onRemove={() => remove(field.name)}
                onAddQuestion={() => add(getInitialQuestion())}
                showAddButton={index === totalFields - 1}
                showRemoveButton={totalFields > 1}
                testResult={testResults?.[index]}
                testError={testErrors?.[index]}
                testInProgress={testingInProgress}
            />
        </Card>
    );
});

const AIQuestionsEditor = ({
                               nameField = 'questions',
                               answerLocationField = 'answerLocation',
                               defaultAnswerLocation = 'record.extraData.aiQuestions',
                               fieldNameRequired = true,
                               showAnswerLocation = true,
                               testQuestion = null,    // Function that tests all questions at once
                               testingEnabled = true,  // Whether testing is enabled
                               testResults = {},       // Object containing test results by question index
                               testingInProgress = false, // Controlled externally
                               testError = null,       // Global test error
                           }) => {
    const [errorMessage, setErrorMessage] = useState(null);
    const form = Form.useFormInstance();

    // Effect to update error message from props
    useEffect(() => {
        setErrorMessage(testError);
    }, [testError]);

    // Get initial data structure for a new question
    const getInitialQuestion = useCallback((index = 0) => ({
        fieldName: `Question_${index + 1}`,
        question: '',
        type: 'string',
        allowMultiple: false,
        allowedValues: [],
        pattern: '',
    }), []);

    // Memoized add question handler to avoid recreating function references
    const handleAddQuestion = useCallback(() => {
        const questions = form.getFieldValue(nameField) || [];
        form.setFieldValue(nameField, [...questions, getInitialQuestion(questions.length)]);
    }, [form, nameField, getInitialQuestion]);

    // Optimization: Only re-render top action buttons when testing state changes
    const TestAllButton = memo(({ onTest, isLoading }) => (
        <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={onTest}
            loading={isLoading}
            size="small"
        >
            Test All Questions
        </Button>
    ));

    return (
        <div className="ai-questions-editor">
            <Button
                type="text"
                icon={<BugOutlined />}
                size="small"
                onClick={() => console.dir(form.getFieldsValue(true))}
                title="Dump form state to console"
            />
            {showAnswerLocation && (
                <Form.Item
                    name={answerLocationField}
                    label="Answer Location"
                    initialValue={defaultAnswerLocation}
                    tooltip={{ title: "Path where question answers will be stored" }}
                    rules={[{ required: true, message: 'Answer location is required' }]}
                    style={{ marginBottom: '16px' }}
                >
                    <Input placeholder={defaultAnswerLocation} />
                </Form.Item>
            )}

            <Row justify="end" align="middle" style={{ marginBottom: '12px' }}>
                {testingEnabled && testQuestion && (
                    <Col>
                        <TestAllButton
                            onTest={testQuestion}
                            isLoading={testingInProgress}
                        />
                    </Col>
                )}
            </Row>

            {errorMessage && (
                <Alert
                    message={errorMessage}
                    type="error"
                    showIcon
                    style={{ marginBottom: '16px' }}
                />
            )}

            <Form.List name={nameField}
                       initialValue={[getInitialQuestion(0)]}
            >
                {(fields, { add, remove }) => (
                    <div className="questions-list-container" style={{
                        position: 'relative',
                        maxHeight: '400px',
                        overflowY: 'auto',
                        padding: '8px 0'
                    }}>
                        {testingInProgress && (
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'rgba(255, 255, 255, 0.7)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                zIndex: 1000
                            }}>
                                <Spin size="large" tip="Testing questions..." />
                            </div>
                        )}

                        {fields.map((field, index) => (
                            <QuestionItem
                                key={field.key}
                                field={field}
                                index={index}
                                totalFields={fields.length}
                                remove={remove}
                                add={() => add(getInitialQuestion(fields.length))}
                                getInitialQuestion={getInitialQuestion}
                                testResults={testResults}
                                testErrors={{}} // Individual errors object if needed
                                testingInProgress={testingInProgress}
                            />
                        ))}

                        {fields.length === 0 && (
                            <Alert
                                message="No questions defined"
                                description="Add a question to get started."
                                type="info"
                                showIcon
                                style={{ marginBottom: '16px' }}
                            />
                        )}
                    </div>
                )}
            </Form.List>

            <Form.Item style={{ marginBottom: '8px' }}>
                <Button
                    type="dashed"
                    onClick={handleAddQuestion}
                    icon={<PlusOutlined />}
                    style={{ width: '100%' }}
                >
                    Add Question
                </Button>
            </Form.Item>

            {testingEnabled && !testQuestion && (
                <Alert
                    message="Testing is not available"
                    description="No testing function has been provided."
                    type="warning"
                    showIcon
                    style={{ marginTop: '16px' }}
                />
            )}
        </div>
    );
};

export default AIQuestionsEditor;
